import React from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../theme/ThemeContext';
import ThemedText from '../components/common/ThemedText';

const TermsOfUseScreen: React.FC = () => {
  const navigation = useNavigation();
  const { theme } = useTheme();

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Terms of Use</ThemedText>
        <View style={styles.headerRight} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.section}>
          <ThemedText style={[styles.lastUpdated, { color: theme.textSecondary }]}>
            Last Updated: January 2026
          </ThemedText>

          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            Welcome to LNB. These Terms of Use ("Terms") govern your access to and use of the LNB mobile application operated by LNM Global Pvt Ltd. By accessing or using our application, you agree to be bound by these Terms.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>1. Acceptance of Terms</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            By creating an account or using LNB, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree, please do not use our services.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>2. Eligibility</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            You must be at least 18 years old to use LNB. By using our services, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>3. User Accounts</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            • You are responsible for maintaining the confidentiality of your account credentials{'\n'}
            • You must provide accurate and complete information{'\n'}
            • You are responsible for all activities under your account{'\n'}
            • You must notify us immediately of any unauthorized use{'\n'}
            • One person may maintain only one account
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>4. Acceptable Use</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            You agree NOT to:{'\n'}
            • Post false, misleading, or fraudulent content{'\n'}
            • Harass, abuse, or harm other users{'\n'}
            • Impersonate any person or entity{'\n'}
            • Violate any applicable laws or regulations{'\n'}
            • Spam or send unsolicited messages{'\n'}
            • Upload viruses or malicious code{'\n'}
            • Scrape or collect user data without permission{'\n'}
            • Use the platform for illegal activities
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>5. User Content</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            • You retain ownership of content you post{'\n'}
            • You grant us a license to use, display, and distribute your content{'\n'}
            • You are responsible for your content and any consequences{'\n'}
            • We may remove content that violates these Terms{'\n'}
            • You must have rights to any content you post
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>6. Professional Networking</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            LNB is designed for professional networking and business activities. You agree to use the platform in a professional manner and respect other users' professional boundaries.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>7. Intellectual Property</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            All rights, title, and interest in the LNB application, including all intellectual property rights, belong to LNM Global Pvt Ltd. You may not copy, modify, distribute, or create derivative works without our written permission.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>8. Third-Party Services</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            Our application may contain links to third-party websites or services. We are not responsible for the content, privacy policies, or practices of third-party services.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>9. Termination</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            We reserve the right to suspend or terminate your account at any time for:{'\n'}
            • Violation of these Terms{'\n'}
            • Fraudulent or illegal activity{'\n'}
            • Extended inactivity{'\n'}
            • At our sole discretion{'\n\n'}
            You may delete your account at any time through the app settings.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>10. Disclaimer of Warranties</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            LNB is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the service will be uninterrupted, secure, or error-free.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>11. Limitation of Liability</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            To the maximum extent permitted by law, LNM Global Pvt Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the application.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>12. Indemnification</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            You agree to indemnify and hold harmless LNM Global Pvt Ltd from any claims, damages, losses, or expenses arising from your use of the service or violation of these Terms.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>13. Privacy</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            Your use of LNB is also governed by our Privacy Policy. Please review our Privacy Policy to understand our data practices.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>14. Modifications to Terms</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            We reserve the right to modify these Terms at any time. We will notify users of material changes. Continued use of the service after changes constitutes acceptance of the modified Terms.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>15. Governing Law and Dispute Resolution</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            These Terms are governed by the laws of India. Any disputes shall be resolved through arbitration in accordance with the Arbitration and Conciliation Act, 1996, in [City], India. The language of arbitration shall be English.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>16. Contact Information</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            For questions about these Terms, please contact:{'\n\n'}
            LNM Global Pvt Ltd{'\n'}
            Email: posteditorlnf@gmail.com
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>17. Severability</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms remain in full force and effect.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>18. Entire Agreement</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            These Terms constitute the entire agreement between you and LNM Global Pvt Ltd regarding the use of LNB and supersede all prior agreements and understandings.
          </ThemedText>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  headerRight: {
    width: 32,
  },
  content: {
    flex: 1,
  },
  section: {
    padding: 20,
  },
  lastUpdated: {
    fontSize: 12,
    marginBottom: 16,
    fontStyle: 'italic',
  },
  heading: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 24,
    marginBottom: 12,
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 12,
  },
  bottomPadding: {
    height: 40,
  },
});

export default TermsOfUseScreen;
